import { useEffect, useState } from "react";
import { launchSummary } from "@buildlab/codeview";
import { api, type ProjectDetail, type ProjectSummary } from "./api";
import { useFeature } from "./features";
import { LaunchQR } from "./LaunchQR";

// Launch Day kit (teacher only, printing only): "I thought I could" cards for every student, and a
// launch card for one finished project. The printed page is set up with print CSS (styles.css,
// "launch kit"); data-print on the overlay picks which sheet prints.

type PrintMode = "front" | "back" | "launch";

/** The Home-page entry point. */
export function LaunchKitEntry() {
  const [open, setOpen] = useState(false);
  return (
    <section aria-label="Launch Day kit" className="subs-entry">
      <div>
        <h2>Launch Day kit</h2>
        <p className="muted small">Printable cards for launch day: an “I thought I could” card for each student, and a launch card for a finished project.</p>
      </div>
      <button type="button" className="outline" onClick={() => setOpen(true)}>
        Open Launch Day kit
      </button>
      {open && <LaunchKit onClose={() => setOpen(false)} />}
    </section>
  );
}

function Blank({ wide }: { wide?: boolean }) {
  return <span className={wide ? "kit-blank wide" : "kit-blank"} aria-hidden="true" />;
}

function FrontCard() {
  return (
    <article className="kit-card">
      <p className="kit-eyebrow">BuildLab</p>
      <h3 className="kit-title">I thought I could</h3>
      <p className="kit-line">
        Before BuildLab I thought I could build <Blank wide /> . Date <Blank />
      </p>
    </article>
  );
}

function BackCard() {
  return (
    <article className="kit-card kit-back">
      <p className="kit-line">
        Now I’ve built <Blank wide /> for <Blank wide /> .
      </p>
      <p className="kit-line">
        What I decided that the helper couldn’t: <Blank wide /> .
      </p>
      <p className="kit-line">
        Next I want to build <Blank wide /> .
      </p>
    </article>
  );
}

function LaunchCard({ title, files, decided, onDecided, showUrl }: { title: string; files: Record<string, string>; decided: string; onDecided: (v: string) => void; showUrl?: string }) {
  const s = launchSummary(files);
  return (
    <article className="kit-launch">
      <p className="kit-eyebrow">BuildLab launch</p>
      <h2 className="kit-launch-title">{title}</h2>
      <p className="kit-for">
        <b>For:</b> {s.forWhom ?? <Blank wide />}
      </p>
      <section>
        <h3 className="kit-label">My must-haves (MVP){s.total > 0 && ` · ${s.done} of ${s.total} done`}</h3>
        {s.total === 0 ? (
          <p className="kit-muted">This project has no checklist in PROJECT.md yet.</p>
        ) : (
          <ul className="kit-mvp">
            {s.mvp.map((m) => (
              <li key={m.text}>
                <span className={m.done ? "kit-box on" : "kit-box"} aria-hidden="true">
                  {m.done ? "✓" : ""}
                </span>
                {m.text}
              </li>
            ))}
            {s.more > 0 && <li className="kit-muted">and {s.more} more</li>}
          </ul>
        )}
      </section>
      <div className="kit-bottom">
        <section className="kit-decided">
          <h3 className="kit-label">What I decided</h3>
          <textarea rows={6} aria-label="What I decided" value={decided} onChange={(e) => onDecided(e.target.value)} />
        </section>
        <LaunchQR url={showUrl} />
      </div>
    </article>
  );
}

function LaunchKit({ onClose }: { onClose: () => void }) {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [picked, setPicked] = useState("");
  const [detail, setDetail] = useState<ProjectDetail["project"] | null>(null);
  const [decided, setDecided] = useState("");
  const [printing, setPrinting] = useState<PrintMode | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The QR code points at this project's newest live family link, when Show links is on.
  const showLinksOn = useFeature("showLinks");
  const [showUrl, setShowUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    setShowUrl(undefined);
    if (!picked || !showLinksOn) return;
    let live = true;
    api.showLinks().then(
      (r) => {
        const link = r.links.find((l) => l.projectId === picked && l.state === "active");
        if (live && link) setShowUrl(`${window.location.origin}/s/${link.token}`);
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [picked, showLinksOn]);

  useEffect(() => {
    void api.projects().then((r) => setProjects(r.projects), (e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    setDetail(null);
    setDecided("");
    if (!picked) return;
    let live = true;
    api.project(picked).then(
      (r) => live && setDetail(r.project),
      (e: Error) => live && setError(e.message),
    );
    return () => {
      live = false;
    };
  }, [picked]);

  // Escape closes; the print dialog closing clears the one-sheet print mode.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const done = () => setPrinting(null);
    window.addEventListener("keydown", onKey);
    window.addEventListener("afterprint", done);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("afterprint", done);
    };
  }, [onClose]);

  // Let React draw the one sheet before the print dialog reads the page.
  const print = (mode: PrintMode) => {
    setPrinting(mode);
    window.setTimeout(() => window.print(), 50);
  };

  return (
    <div className="launch-backdrop">
      <div className="launch-kit" role="dialog" aria-modal="true" aria-label="Launch Day kit" data-print={printing ?? undefined}>
        <header className="launch-bar no-print">
          <div className="launch-title">
            <h2>Launch Day kit</h2>
            <button type="button" className="icon-close" aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="launch-actions">
            <button type="button" className="outline small" onClick={() => print("front")}>
              Print “I thought I could” fronts
            </button>
            <button type="button" className="outline small" onClick={() => print("back")}>
              Print backs
            </button>
            <label className="launch-pick">
              <span>Launch card for</span>
              <select value={picked} onChange={(e) => setPicked(e.target.value)}>
                <option value="">Choose a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="primary small" disabled={!detail} onClick={() => print("launch")}>
              Print launch card
            </button>
          </div>
          {error && <p className="page-error">{error}</p>}
          <p className="muted small">Each card sheet has four cards. Print the fronts and the backs on separate sheets, or on both sides of one sheet. The launch card prints one to a page.</p>
        </header>

        <section className="kit-sheet kit-front" aria-label="Card fronts">
          <p className="kit-sheet-label no-print">Fronts</p>
          <div className="kit-grid">
            {[0, 1, 2, 3].map((i) => (
              <FrontCard key={i} />
            ))}
          </div>
        </section>

        <section className="kit-sheet kit-back" aria-label="Card backs">
          <p className="kit-sheet-label no-print">Backs</p>
          <div className="kit-grid">
            {[0, 1, 2, 3].map((i) => (
              <BackCard key={i} />
            ))}
          </div>
        </section>

        {detail && (
          <section className="kit-launch-page" aria-label="Launch card">
            <p className="kit-sheet-label no-print">Launch card</p>
            <LaunchCard title={detail.title} files={detail.files} decided={decided} onDecided={setDecided} showUrl={showUrl} />
          </section>
        )}
      </div>
    </div>
  );
}
